/**
 * Pure helpers for the family-columns board draft (moved from WSRADM-0027 DimensionBoard).
 *
 * GET always uses includeRetired=true; the PUT body is the full membership set; seed from a
 * PUT echo only when isCompleteEcho() holds.
 */
import type { BoardProduct, DimensionBoard as DimensionBoardDto } from '@/api/dimensions/types';
import { ApiError } from '@/api/errors';
import { detailsCode } from './detailsCode';

export type SaveErrorKey = 'staleBoard' | 'dimensionGone' | 'readOnly' | 'tooLarge' | 'saveFailed';

export type Draft = Map<number, Set<string>>;

export const SAVE_ERROR_FALLBACK: Record<SaveErrorKey, string> = {
  staleBoard: 'The board changed on the server. Reload and try again.',
  dimensionGone: 'This dimension no longer exists.',
  tooLarge: 'This board is too large to load.',
  readOnly: 'This dimension is read-only.',
  saveFailed: 'Could not save the board.',
};

export function seedDraft(board: DimensionBoardDto): Draft {
  const draft: Draft = new Map();
  for (const product of board.products) {
    draft.set(product.sourceProductId, new Set(product.familySlugs));
  }
  return draft;
}

/** True when every product the client loaded is present in the PUT echo. A missing product
 *  means the server re-read without includeRetired — seeding from it would drop retired
 *  allocations, so the caller must refetch instead. */
export function isCompleteEcho(loaded: DimensionBoardDto, echo: DimensionBoardDto): boolean {
  const ids = new Set(echo.products.map((p) => p.sourceProductId));
  return loaded.products.every((p) => ids.has(p.sourceProductId));
}

export function cloneDraft(draft: Draft): Draft {
  const next: Draft = new Map();
  for (const [id, slugs] of draft) {
    next.set(id, new Set(slugs));
  }
  return next;
}

export function membershipsFromDraft(board: DimensionBoardDto, draft: Draft) {
  const seen = new Set<string>();
  const memberships: Array<{ sourceProductId: number; familySlug: string }> = [];
  for (const product of board.products) {
    const slugs = draft.get(product.sourceProductId);
    if (slugs === undefined) continue;
    for (const familySlug of slugs) {
      const key = `${product.sourceProductId}\0${familySlug}`;
      if (seen.has(key)) continue;
      seen.add(key);
      memberships.push({ sourceProductId: product.sourceProductId, familySlug });
    }
  }
  return memberships;
}

export function distinctCategories(products: BoardProduct[]): string[] {
  const set = new Set<string>();
  for (const product of products) {
    for (const category of product.wstudioCategories) set.add(category);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function mapLoadError(err: unknown): 'dimensionGone' | 'tooLarge' | 'loadFailed' {
  const code = detailsCode(err);
  if (code === 'DIMENSION_NOT_FOUND') return 'dimensionGone';
  if (code === 'BOARD_TOO_LARGE') return 'tooLarge';
  return 'loadFailed';
}

export function mapSaveError(err: unknown): SaveErrorKey {
  const code = detailsCode(err);
  if (
    code === 'UNKNOWN_PRODUCT_ID' ||
    code === 'UNKNOWN_FAMILY_SLUG' ||
    code === 'DUPLICATE_MEMBERSHIP'
  ) {
    return 'staleBoard';
  }
  if (code === 'DIMENSION_NOT_FOUND') return 'dimensionGone';
  if (code === 'DIMENSION_READ_ONLY') return 'readOnly';
  if (code === 'BOARD_TOO_LARGE') return 'tooLarge';
  return 'saveFailed';
}

/** Count how many products gained or lost membership in `familySlug` relative to the seed. */
export function familyAllocChanged(
  seed: Draft,
  draft: Draft,
  familySlug: string,
  productIds: Iterable<number>,
): boolean {
  for (const id of productIds) {
    const was = seed.get(id)?.has(familySlug) === true;
    const now = draft.get(id)?.has(familySlug) === true;
    if (was !== now) return true;
  }
  return false;
}

/** Distinct product ids whose membership set differs from the seed. */
export function countAllocChanges(seed: Draft, draft: Draft): number {
  const ids = new Set<number>([...seed.keys(), ...draft.keys()]);
  let n = 0;
  for (const id of ids) {
    const a = seed.get(id) ?? new Set<string>();
    const b = draft.get(id) ?? new Set<string>();
    if (a.size !== b.size) {
      n += 1;
      continue;
    }
    let same = true;
    for (const slug of a) {
      if (!b.has(slug)) {
        same = false;
        break;
      }
    }
    if (!same) n += 1;
  }
  return n;
}

/**
 * True when a failed save is worth a background re-check: anything that is not a
 * 4xx answer (a 5xx, a network failure or a timeout may have landed server-side;
 * a 4xx was refused outright). WSRADM-0047.
 */
export function isUncertainFailure(err: unknown): boolean {
  return !(err instanceof ApiError && err.status >= 400 && err.status < 500);
}

/**
 * Set equality of `(sourceProductId, familySlug)` pairs between two drafts, each read
 * through `membershipsFromDraft` against its own board (a pair outside that board's
 * products is never part of the PUT).
 */
export function sameMemberships(
  a: { board: DimensionBoardDto; draft: Draft },
  b: { board: DimensionBoardDto; draft: Draft },
): boolean {
  const key = (m: { sourceProductId: number; familySlug: string }) =>
    `${m.sourceProductId}\0${m.familySlug}`;
  const left = new Set(membershipsFromDraft(a.board, a.draft).map(key));
  const right = new Set(membershipsFromDraft(b.board, b.draft).map(key));
  if (left.size !== right.size) return false;
  for (const k of left) if (!right.has(k)) return false;
  return true;
}
